import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { agentService } from '../services/agentService';
import { useContextStore } from '../store/contextStore';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';
import type { AgentDefinitionDto } from '../types/agent';

export const ApiExplorerPage = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const currentTenant = useContextStore((s) => s.currentTenant);

  useEffect(() => {
    loadAgents();
  }, [currentTenant?.id]);

  const loadAgents = async () => {
    setLoading(true);
    try {
      const result = await agentService.getAgents({ pageSize: 100, isActive: true });
      setAgents(result.items ?? []);
    } catch {
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const columns: DataTableColumn<AgentDefinitionDto>[] = [
    {
      id: 'name', label: 'Agent',
      render: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
          <Typography variant="caption" color="text.secondary">{r.description}</Typography>
        </Box>
      ),
    },
    {
      id: 'slug', label: 'Slug',
      render: (r) => <Chip label={r.slug} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 12 }} />,
    },
    {
      id: 'category', label: 'Category',
      render: (r) => r.category ?? '—',
    },
    {
      id: 'model', label: 'Model',
      render: (r) => r.defaultModelName ?? '—',
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <Tooltip title="View API data">
          <IconButton size="small" onClick={() => navigate(`/api-explorer/${r.slug}`)}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <PageListLayout
      title="API Explorer"
      subtitle="Browse Chat Completions, Responses, and Assistants API data per agent."
      breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'API Explorer' }]}
      loading={loading}
      isEmpty={agents.length === 0}
      emptyState={{
        title: 'No Agents Found',
        description: 'Create an agent first, then explore its API data here.',
      }}
    >
      <DataTable
        columns={columns}
        rows={agents}
        loading={loading}
        onRowClick={(r) => navigate(`/api-explorer/${r.slug}`)}
        emptyMessage="No agents available."
      />
    </PageListLayout>
  );
};
